import * as Sentry from '@sentry/react';
import { config } from '@/amplify';
import { isBrave } from './isBrave';
import { isMobileApp } from './isMobileApp';
import { getAppRelease } from './appRelease';
import type { History } from 'history';

// ...existing code...

const SENTRY_LOG_ENVIRONMENTS = new Set(['prod', 'staging']);

export const initSentry = (history: History): void => {
  const sentryEnvironment = import.meta.env.VITE_APP_SENTRY_ENVIRONMENT;
  const sentryLogsEnabled = SENTRY_LOG_ENVIRONMENTS.has(sentryEnvironment);

  Sentry.init({
    dsn: isMobileApp()
      ? import.meta.env.VITE_APP_SENTRY_DSN_MAQUINISTA
      : import.meta.env.VITE_APP_SENTRY_DSN_MANAGER,
    release: getAppRelease(),
    environment: sentryEnvironment,
    beforeBreadcrumb: (breadcrumb, hint) => {
      if (breadcrumb.category === 'xhr') {
        breadcrumb.data = hint?.xhr.__sentry_xhr__;
      }
      return breadcrumb;
    },
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    tracePropagationTargets: [
      'localhost',
      config.AppSync.graphqlEndpoint,
      config.AppSync.websocketsEndpoint,
    ],
    integrations: [
      ...(sentryLogsEnabled
        ? [
            Sentry.consoleLoggingIntegration({
              levels: ['log', 'warn', 'error'],
            }),
          ]
        : []),
      Sentry.reactRouterV5BrowserTracingIntegration({ history }),
      ...(import.meta.env.NODE_ENV === 'production'
        ? [Sentry.replayIntegration()]
        : []),
    ],
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    tracesSampleRate: Number(
      window.localStorage.getItem('BILDERIT_SENTRY_TRACING_RATE') ??
        import.meta.env.VITE_APP_SENTRY_TRACING_RATE ??
        0.1
    ),
    maxValueLength: 2000,
    enableLogs: sentryLogsEnabled,
  });

  // Brave masks its user agent as Chrome, so `browser.name` cannot separate
  // the two and Brave's share of our users was unmeasurable. Tag every event
  // from this feature-detect instead: `browser_brave:true` is queryable
  // alongside any other filter, across all existing errors.
  Sentry.setTag('browser_brave', isBrave() ? 'true' : 'false');
};

const scope = Sentry.getCurrentScope();
if (scope) {
  const tags = scope.getScopeData().tags;
  scope.setTag('AppCrashed', tags.AppCrashed || 'false');
}
